import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, handleAxiosError } from 'lib/api';
import type { MutationConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import { isAxiosError } from 'axios';

interface AddFlowPayload {
  name: string;
  app: string;
  platform: string;
  url: string;
  default_facebook_page_id: number;
  default_creative_text_id: number | null;
  additional_facebook_pages_ids: number[];
  additional_creative_texts_ids: number[];
}

interface UpdateFlowPayload extends AddFlowPayload {
  flow_id: number;
}

interface FlowResponse {
  message: string;
}

interface ErrorResponse {
  message: string;
  details?: string;
}

export const addFlow = async (
  params: AddFlowPayload
): Promise<FlowResponse> => {
  const res = await apiClient.post<FlowResponse>(queryKeys.flows, params);

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to add flow');
  }
  return res.data;
};

export const updateFlow = async ({
  flow_id,
  ...params
}: UpdateFlowPayload): Promise<FlowResponse> => {
  const res = await apiClient.put<FlowResponse>(
    `${queryKeys.flows}/${flow_id}`,
    params
  );

  if (isAxiosError<ErrorResponse>(res)) {
    return handleAxiosError(res, 'Failed to update flow');
  }
  return res.data;
};

type MutationFnType = (data: AddFlowPayload) => ReturnType<typeof addFlow>;
type UpdateMutationFnType = (
  data: UpdateFlowPayload
) => ReturnType<typeof updateFlow>;

interface UseAddFlowOptions {
  config?: MutationConfig<MutationFnType>;
}

interface UseUpdateFlowOptions {
  config?: MutationConfig<UpdateMutationFnType>;
}

export const useAddFlow = ({ config }: UseAddFlowOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.flows],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: addFlow,
  });
};

export const useUpdateFlow = ({ config }: UseUpdateFlowOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.flows],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.flowDetails],
        }),
      ]);

      if (config?.onSuccess) {
        await config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: updateFlow,
  });
};
